/**
  * @class meleDatetime
  * @description
  *
  * # Purpose
  * Implements `<mele-datetime>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-datetime></mele-datetime>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-datetime',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'
const GRID_DAYS = 42

const PROPERTIES = _.extend({}, {
  time: { type: 'number', description: 'Unix timestamp of selected time' },
  mintime: { type: 'number', description: 'Unix timestamp of minimal time' },
  maxtime: { type: 'number', description: 'Unix timestamp of maximum time' },
  hideheader: { type: 'boolean', defualt: false },
  mode: { type: 'string', description: 'show header', enum: ['date', 'time'], default: 'date' },
  selected: { type: 'array', description: 'Selected date as date' },
  eventDatetimeChanged: { type: 'function', description: 'called when calendar content change' },
  eventDatetimeDone: { type: 'function', description: 'called when last input selected' },
}, meleElement._properties)

export default class meleDatetime extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    this.timer = null

    this.elHeader = this.appendChildren({
      tag: 'div',
      class: 'tabs',
      children: [
        { tag: 'mele-button', key: 'date', class: 'month-change', iconleft: 'u.calendar', eventButtonClicked: this.changeTab.bind(this) },
        { tag: 'mele-text', key: 'current_date' },
        { tag: 'mele-button', key: 'time', class: 'month-change', iconleft: 'dt.clock', eventButtonClicked: this.changeTab.bind(this) },
      ],
    })

    this.elGridNav = this.appendChildren({
      tag: 'div',
      class: 'nav',
      children: [
        { tag: 'mele-button', key: 'prev', class: 'month-change', iconleft: 'direction.w', eventButtonClicked: this.changeMonth.bind(this) },
        { tag: 'mele-text', key: 'month_name', class: 'month_name' },
        { tag: 'mele-button', key: 'next', class: 'month-change', iconleft: 'direction.e', eventButtonClicked: this.changeMonth.bind(this) },
      ],
    })

    this.elListNav = this.appendChildren({
      tag: 'div',
      class: 'nav names',
      children: [
        { tag: 'mele-text', label: 'calendar.hour' },
        { tag: 'mele-text', label: 'calendar.minute' },
        { tag: 'mele-text', label: 'calendar.second' },
      ],
    })

    this.elMonth = this.find('@month_name')
    this.elCurrent = this.find('@current_date')

    this.elGrid = this.appendChildren({ tag: 'div', class: 'grid' })
    this.elGrid.onclick = (e)=>{
      const el = e.target.closest('calendar-item')
      if(!el) return
      const key = el.getAttribute('key')
      if(!key) return
      this.changeSelected(key, true)
    }
    this.elList = this.appendChildren({ tag: 'div', class: 'list' })
    this.renderTime()
    this.modeChanged(this.mode, this.mode)
  } // init

  changeTab(self) {
    this.mode = self.key
  }

  modeChanged(value, old) {
    if(value == 'date') {
      this.elGrid.hidden = false
      this.elList.hidden = true
      this.elGridNav.hidden = false
      this.elListNav.hidden = true
    } else if(value == 'time') {
      this.elGrid.hidden = true
      this.elList.hidden = false
      this.elGridNav.hidden = true
      this.elListNav.hidden = false
    }
  }

  hideheaderChanged(value, old) {
    this.elHeader.hidden = value
  }

  mintimeChanged(value, old) {
    this._min = this.timestampToArray(value).slice(0, 3)
    if(this.selected.length == 0) return
    this.drawGridItems(this.selected)
  }

  maxtimeChanged(value, old) {
    this._max = this.timestampToArray(value).slice(0, 3)
    if(this.selected.length == 0) return
    this.drawGridItems(this.selected)
  }

  drawGridItems(selected) {
    if(this.elMonth) {
      this.elMonth.label = this.translate('Month.'+(selected[1]+1)) + ' ' + selected[0]
    }
    this.calculateLimits(selected)
    this.elGrid.innerHTML = ''
    this.elGrid.appendChildren(this._items)
  }

  calculateLimits(selected) {
    const current_month = selected[1]
    const current_year = selected[0]
    const new_items = []

    const [min_year, min_month, min_day] = this._min || []
    const [max_year, max_month, max_day] = this._max || []

    for (const item of this._items) {
      const item_key = item.key || item.getAttribute('key')
      const [year, month, day] = item_key.split('-').map(k => parseInt(k))

      let shouldGrayOut = false

      // Check against minimum time
      if (this.mintime && (year < min_year || (year == min_year && month < min_month))) {
        shouldGrayOut = true
      } else if (year == min_year && month == min_month && day < min_day) {
        shouldGrayOut = true
      }

      // Check against maximum time
      if (this.maxtime && (year > max_year || (year == max_year && month > max_month))) {
        shouldGrayOut = true
      } else if (year == max_year && month == max_month && day > max_day) {
        shouldGrayOut = true
      }

      // Check if it's not the current month in the current year
      if (year != current_year || month != current_month) {
        shouldGrayOut = true
      }

      item.class = shouldGrayOut ? 'gray' : ''

      new_items.push(item)
    }

    this._items = new_items
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "time"
  */
  timeChanged(value, old) {
    if(!value) return
    if(this.mintime || this.maxtime) {
      if(this.maxtime && value > this.maxtime) {
        value = this.maxtime
      }

      if(this.mintime && value < this.mintime) {
        value = this.mintime
      }
    }
    const selected = this.timestampToArray(value)
    this.elCurrent.label = new Date(value * 1000).toLocaleString('LT')
    this.setProperty('selected', selected, true)
  } // timeChanged

  timestampToArray(timestamp) {
    const dt = new Date(timestamp * 1000) // timestamp is unix value
    return [
      dt.getFullYear(),
      dt.getMonth(),
      dt.getDate(),
      dt.getHours(),
      dt.getMinutes(),
      dt.getSeconds(),
    ]
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "selected"
  */
  selectedChanged(value, old, skip_setting_time) {
    if(!old || value[0] != old[0] || value[1] != old[1]) {
      this.renderDate(value)
    }

    if(old[2] != value[2]) {
      this.changeSelected(`${value[0]}-${value[1]}-${value[2]}`)
    }

    if(value[3] != old[3] || value[4] != old[4] || value[5] != old[5]) {
      this.changeSelectedTime(value[3], value[4], value[5])
    }

    if(!skip_setting_time) {
      this.setProperty('time', _.roundBy(new Date(...value).valueOf()/1000, 3))
    }

    if(typeof (this.eventCalendarChanged) === 'function') {
      this.eventCalendarChanged(this, this.time, skip_setting_time)
    }
  } // selectedChanged

  renderDate(selected) {
    const [year, month, day] = selected
    const previous_month = this.getDaysInMonth(year, month-1)
    const current_month = this.getDaysInMonth(year, month)
    const next_month = this.getDaysInMonth(year, month+1)

    const first_day = new Date(year, month, 1).getDay()

    const previous_days = this.createDaysInMonth(...this._calcMonthShift(year, month-1), previous_month)
    const current_days = this.createDaysInMonth(year, month, current_month, true)
    const next_days = this.createDaysInMonth(...this._calcMonthShift(year, month+1), next_month)

    let short_previous_month
    // sunday is start of month
    if(first_day == 0) {
      short_previous_month = previous_days.slice(-6)
    } else if(first_day == 1) {
      short_previous_month = previous_days.slice(-7)
    } else if(first_day > 1) {
      short_previous_month = previous_days.slice((first_day-1) * -1)
    }

    const short_next_month = next_days.slice(0, GRID_DAYS - (short_previous_month.length + current_month))
    this.elGrid.innerHTML = ''
    this._items = [...short_previous_month, ...current_days, ...short_next_month]
    const translatedMonth = this.translate('Month.'+(month+1)) // month is zero indexed
    this.elMonth.label = translatedMonth + ' ' + year
    this.drawGridItems(selected)
    this.changeSelected([year, month, day].join('-'))
  }

  getDaysInMonth(year, month) {
    return new Date(year, month+1, 0).getDate()
  }

  createDaysInMonth(year, month, days) {
    const res = []
    for(let day = 1; day <= days; day++) {
      const key = [year, month, day]
      res.push({
        tag: 'calendar-item',
        key: key.join('-'),
        label: day,
      })
    }
    return res
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * changes the month
  */
  changeMonth(self) {
    if(!self.key) return
    const selected = [...this.selected]
    selected[1] = self.key == 'next' ? selected[1]+1 : selected[1]-1
    const [year, month] = this._calcMonthShift(selected[0], selected[1])

    // Get min and max years and months
    const [min_year, min_month] = this._min || []
    const [max_year, max_month] = this._max || []

    // Initialize a flag to indicate if the selected date is valid
    let isValidDate = true

    // Check if the new selected month and year are within the min dates
    if (this._min && (year < min_year || (year === min_year && month < min_month))) {
      isValidDate = false
    }

    // Check if the new selected month and year are within the max dates
    if (this._max && (year > max_year || (year === max_year && month > max_month))) {
      isValidDate = false
    }

    // If the selected date is valid, update the selected date
    if (isValidDate) {
      selected[0] = year
      selected[1] = month
      this.selected = selected
    }
  } // changeMonth

  _calcMonthShift(year, month) {
    if(month == 12) {
      month = 0
      year = year+1
    } else if(month== -1) {
      month = 11
      year = year-1
    }

    return [year, month]
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * changes selected item. removes old selected, and set's the new one.
  */
  changeSelected(key, from_event) {
    this.elGrid.forAll('calendar-item', el=>{
      el.selected = el.key == key
    })

    this._items.forEach(item=>{
      item.selected = item.key == key
    })

    if(from_event) {
      const keys = key.split('-').map(k=>parseInt(k))
      const old_selected = [...this.selected]
      this.selected = [...keys, ...old_selected.slice(keys.length)]
    }
  } // changeSelected

  scrollAll() {
    setTimeout(()=> {
      this.prevented = true
      this.elList.forAll('.column', column => {
        if(column.offsetHeight == 0) return
        const selected = column.find('[selected]')
        if(!selected) return
        const key = parseInt(selected.getAttribute('key'))
        column.scrollTo({
          top: 28.1 * key,
          behavior: 'smooth',
        })
      })
      this.prevented = false
    }, 50)
  }

  changeSelectedTime(hours, minutes, seconds) {
    this.elList.forAll('.hours > calendar-item', el=>{
      const key = el.getAttribute('key')
      if(key == hours) {
        el.setAttribute('selected', '')
      } else {
        el.removeAttribute('selected')
      }
    })

    this.elList.forAll('.minutes > calendar-item', el=>{
      const key = el.getAttribute('key')
      if(key == minutes) {
        el.setAttribute('selected', '')
      } else if(el.hasAttribute('selected')) {
        el.removeAttribute('selected')
      }
    })

    this.elList.forAll('.seconds > calendar-item', el=>{
      const key = el.getAttribute('key')
      if(key == seconds) {
        el.setAttribute('selected', '')
      } else {
        el.removeAttribute('selected')
      }
    })

    this.scrollAll()
  }

  renderTime() {
    const hours = 24
    const minutes = 60
    const seconds = 60

    const res_hours = []
    for(let hour = 0; hour < hours; hour++) {
      res_hours.push({
        tag: 'calendar-item',
        key: hour,
        label: hour,
      })
    }

    const res_minutes = []
    for(let minute = 0; minute < minutes; minute++) {
      res_minutes.push({
        tag: 'calendar-item',
        key: minute,
        label: minute,
      })
    }

    const res_seconds = []
    for(let second = 0; second < seconds; second++) {
      res_seconds.push({
        tag: 'calendar-item',
        key: second,
        label: second,
      })
    }
    this.elList.appendChildren([
      { tag: 'div', class: 'hours column', part: 3, children: res_hours, onscroll: this.scrollSnapSelect.bind(this) },
      { tag: 'div', class: 'minutes column', part: 4, children: res_minutes, onscroll: this.scrollSnapSelect.bind(this) },
      { tag: 'div', class: 'seconds column', part: 5, children: res_seconds, onscroll: this.scrollSnapSelect.bind(this) },
    ])
  }

  scrollSnapSelect(e) {
    if(this.prevented) return
    const offset = 2
    clearTimeout(this.timer)
    this.timer = setTimeout(()=>{
      const scroll_position = e.target.scrollTop - offset
      const item_height = e.target.querySelector('calendar-item').offsetHeight
      const part = e.target.getAttribute('part')
      const selectedId = parseInt(scroll_position / item_height)
      const new_selected = [...this.selected]
      new_selected[part] = selectedId
      this.setProperty('selected', new_selected, false)
    }, 500)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle event "CalendarChanged"
  */
  eventCalendarChanged(el, time, tab) {
  } // eventCalendarChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle event "CalendarDone"
  */
  eventCalendarDone(self) {
  } // eventCalendarDone
} // class meleDatetime

meleDatetime._properties = PROPERTIES
meleDatetime.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-datetime', meleDatetime)

const CALENDAR_ITEM_PROPERTIES = _.extend({}, {
  selected: { type: 'boolean', description: 'Unix timestamp of selected time' },
  label: { type: 'string', description: 'Unix timestamp of selected time' },
}, meleElement._properties)
class calendarItem extends meleElement {
  init() {}
  labelChanged(value) {
    this.innerHTML = `<span>${String(value).padStart(2, '0')}</span>`
  }
}

calendarItem._properties = CALENDAR_ITEM_PROPERTIES
calendarItem.observedAttributes = _.keys(CALENDAR_ITEM_PROPERTIES)
window.customElements.define('calendar-item', calendarItem)
